import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Input, Switch, Tooltip, Dropdown, Button, Modal, Space } from 'antd';
import {
  BoldOutlined, ItalicOutlined, UnderlineOutlined,
  OrderedListOutlined, UnorderedListOutlined, LinkOutlined,
  AlignLeftOutlined, AlignCenterOutlined, AlignRightOutlined,
  ClearOutlined, FontSizeOutlined, TagOutlined
} from '@ant-design/icons';

const { TextArea } = Input;

const DEFAULT_VARIABLES = [
  { key: 'firstName', label: 'First name' },
  { key: 'lastName', label: 'Last name' },
  { key: 'email', label: 'Email' },
  { key: 'surveyUrl', label: 'Survey URL' }
];

/**
 * EmailBodyEditor — visual (WYSIWYG) editor with an HTML toggle.
 * - Visual mode: contentEditable area with a small toolbar. Friendly for non-technical users.
 * - HTML mode: raw textarea for power users.
 *
 * Uses document.execCommand for formatting. It is deprecated, but still
 * universally supported and is the lightest possible solution (no extra deps).
 *
 * Props:
 * - variables: optional [{ key, label }] list for the "Insert variable" menu.
 * - showHeader: set false to hide the built-in "Email Body" header/help text
 *   when embedding inside another labeled container.
 */
const EmailBodyEditor = ({ value, onChange, disabled, variables, showHeader = true }) => {
  const VARIABLES = variables && variables.length ? variables : DEFAULT_VARIABLES;
  const [visualMode, setVisualMode] = useState(true);
  const editorRef = useRef(null);
  const lastEmittedRef = useRef(value || '');

  // Sync incoming value into the contentEditable when it changes externally
  // (e.g. template applied) without disturbing the caret while the user types.
  useEffect(() => {
    if (!visualMode) return;
    const el = editorRef.current;
    if (!el) return;
    if ((value || '') !== lastEmittedRef.current) {
      el.innerHTML = value || '';
      lastEmittedRef.current = value || '';
    }
  }, [value, visualMode]);

  // Initial mount in visual mode — populate
  useEffect(() => {
    if (visualMode && editorRef.current && editorRef.current.innerHTML === '') {
      editorRef.current.innerHTML = value || '';
      lastEmittedRef.current = value || '';
    }
  }, [visualMode, value]);

  const emit = useCallback(() => {
    const html = editorRef.current?.innerHTML || '';
    lastEmittedRef.current = html;
    onChange(html);
  }, [onChange]);

  const exec = (command, arg = null) => {
    if (disabled) return;
    editorRef.current?.focus();
    document.execCommand(command, false, arg);
    emit();
  };

  const insertHtml = (html) => {
    if (disabled) return;
    editorRef.current?.focus();
    document.execCommand('insertHTML', false, html);
    emit();
  };

  const insertVariable = (key) => insertHtml(`{{${key}}}`);

  const handleAddLink = () => {
    Modal.confirm({
      title: 'Insert link',
      content: (
        <div style={{ marginTop: 12 }}>
          <Input id="bha-link-url-input" placeholder="https://example.com" />
        </div>
      ),
      okText: 'Insert',
      onOk: () => {
        const inp = document.getElementById('bha-link-url-input');
        const url = inp?.value?.trim();
        if (url) exec('createLink', url);
      }
    });
  };

  const handleHeading = (tag) => {
    exec('formatBlock', tag);
  };

  return (
    <div>
      {showHeader && (
        <>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 12,
              marginBottom: 6,
              flexWrap: 'wrap'
            }}
          >
            <h3 style={{ margin: 0 }}>Email Body</h3>
            <Space size={8} align="center">
              <span style={{ color: '#6b7280', fontSize: 13 }}>HTML mode</span>
              <Switch
                checked={!visualMode}
                onChange={(checked) => setVisualMode(!checked)}
                disabled={disabled}
              />
            </Space>
          </div>
          <p style={{ color: '#8c8c8c', marginTop: 0, marginBottom: 12, fontSize: 13 }}>
            {visualMode
              ? 'Type your message and use the toolbar to format. Click "Insert variable" to personalize each email.'
              : 'Edit the raw HTML directly. Personalize with variables like {{firstName}} or {{surveyUrl}}.'}
          </p>
        </>
      )}

      {visualMode ? (
        <>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              rowGap: 6,
              padding: '10px 12px',
              border: '1px solid #e5e7eb',
              borderBottom: 'none',
              borderRadius: '8px 8px 0 0',
              background: '#fafbfc'
            }}
          >
            <Dropdown
              disabled={disabled}
              menu={{
                items: [
                  { key: 'p', label: 'Paragraph', onClick: () => handleHeading('p') },
                  { key: 'h1', label: <strong style={{ fontSize: 18 }}>Heading 1</strong>, onClick: () => handleHeading('h1') },
                  { key: 'h2', label: <strong style={{ fontSize: 16 }}>Heading 2</strong>, onClick: () => handleHeading('h2') },
                  { key: 'h3', label: <strong style={{ fontSize: 14 }}>Heading 3</strong>, onClick: () => handleHeading('h3') }
                ]
              }}
            >
              <Button icon={<FontSizeOutlined />} style={{ marginRight: 6 }}>
                Style
              </Button>
            </Dropdown>

            <Space.Compact style={{ marginRight: 6 }}>
              <Tooltip title="Bold (Ctrl+B)"><Button icon={<BoldOutlined />} onClick={() => exec('bold')} disabled={disabled} /></Tooltip>
              <Tooltip title="Italic (Ctrl+I)"><Button icon={<ItalicOutlined />} onClick={() => exec('italic')} disabled={disabled} /></Tooltip>
              <Tooltip title="Underline (Ctrl+U)"><Button icon={<UnderlineOutlined />} onClick={() => exec('underline')} disabled={disabled} /></Tooltip>
            </Space.Compact>

            <Space.Compact style={{ marginRight: 6 }}>
              <Tooltip title="Bulleted list"><Button icon={<UnorderedListOutlined />} onClick={() => exec('insertUnorderedList')} disabled={disabled} /></Tooltip>
              <Tooltip title="Numbered list"><Button icon={<OrderedListOutlined />} onClick={() => exec('insertOrderedList')} disabled={disabled} /></Tooltip>
            </Space.Compact>

            <Space.Compact style={{ marginRight: 6 }}>
              <Tooltip title="Align left"><Button icon={<AlignLeftOutlined />} onClick={() => exec('justifyLeft')} disabled={disabled} /></Tooltip>
              <Tooltip title="Align center"><Button icon={<AlignCenterOutlined />} onClick={() => exec('justifyCenter')} disabled={disabled} /></Tooltip>
              <Tooltip title="Align right"><Button icon={<AlignRightOutlined />} onClick={() => exec('justifyRight')} disabled={disabled} /></Tooltip>
            </Space.Compact>

            <Tooltip title="Insert link">
              <Button icon={<LinkOutlined />} onClick={handleAddLink} disabled={disabled} style={{ marginRight: 6 }} />
            </Tooltip>

            <Dropdown
              disabled={disabled}
              menu={{
                items: VARIABLES.map((v) => ({
                  key: v.key,
                  label: <span><code>{`{{${v.key}}}`}</code> &nbsp;<span style={{ color: '#8c8c8c' }}>{v.label}</span></span>,
                  onClick: () => insertVariable(v.key)
                }))
              }}
            >
              <Button icon={<TagOutlined />}>Insert variable</Button>
            </Dropdown>

            <span style={{ flex: 1, minWidth: 8 }} />

            <Tooltip title="Clear formatting">
              <Button icon={<ClearOutlined />} onClick={() => exec('removeFormat')} disabled={disabled} />
            </Tooltip>
          </div>

          <div
            ref={editorRef}
            contentEditable={!disabled}
            suppressContentEditableWarning
            onInput={emit}
            onBlur={emit}
            style={{
              minHeight: 300,
              maxHeight: 520,
              overflowY: 'auto',
              padding: '16px 20px',
              border: '1px solid #e5e7eb',
              borderRadius: '0 0 8px 8px',
              background: disabled ? '#f5f5f5' : '#fff',
              fontSize: 14,
              lineHeight: 1.6,
              color: '#1f2937',
              outline: 'none'
            }}
          />
        </>
      ) : (
        <TextArea
          rows={16}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{ fontFamily: 'monospace', fontSize: 13 }}
          disabled={disabled}
        />
      )}
    </div>
  );
};

export default EmailBodyEditor;
